import { useEffect, useState, Component, type ReactNode } from 'react'
import { useQuery, useAction } from 'convex/react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../../../../../convex/_generated/api'
import { Id } from '../../../../../convex/_generated/dataModel'
import { useAuth } from '@/contexts/auth-context'
import { AlertTriangle, ArrowLeft, ExternalLink, Loader2 } from 'lucide-react'

const POSTHOG_PROJECT_ID = '198125'

function formatDate(ts: number) {
  return new Date(ts).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

class SessionErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null }
  static getDerivedStateFromError(error: Error) { return { error } }
  render() {
    if (this.state.error) {
      return (
        <div className="flex h-screen flex-col items-center justify-center bg-neutral-950 text-neutral-400">
          <AlertTriangle className="mb-3 h-8 w-8 text-amber-500" />
          <p className="text-sm">Failed to load session recording</p>
          <p className="mt-1 text-xs text-neutral-600">The session ID may be invalid or the data is unavailable.</p>
          <a href="/admin/sessions" className="mt-4 text-xs text-indigo-400 hover:text-indigo-300">Back to sessions</a>
        </div>
      )
    }
    return this.props.children
  }
}

export default function AdminSessionReplayPage() {
  return (
    <SessionErrorBoundary>
      <AdminSessionReplayInner />
    </SessionErrorBoundary>
  )
}

function AdminSessionReplayInner() {
  const { user, isAuthenticated } = useAuth()
  const isAdmin = isAuthenticated && user?.isAdmin
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const recordingId = id as Id<'sessionRecordings'>

  const recording = useQuery(
    api.sessionRecordings.adminGetRecording,
    isAdmin && recordingId ? { recordingId } : 'skip'
  )

  const getEmbedToken = useAction(api.posthog.getEmbedToken)
  const [embedToken, setEmbedToken] = useState<string | null>(null)
  const [embedError, setEmbedError] = useState<string | null>(null)

  useEffect(() => {
    if (!isAdmin || !recording?.posthogSessionId) return
    let cancelled = false
    setEmbedToken(null)
    setEmbedError(null)
    getEmbedToken({ posthogSessionId: recording.posthogSessionId })
      .then(({ accessToken }) => {
        if (!cancelled) setEmbedToken(accessToken)
      })
      .catch((e: any) => {
        if (!cancelled) setEmbedError(e?.message || 'Failed to load PostHog embed')
      })
    return () => { cancelled = true }
  }, [isAdmin, recording?.posthogSessionId, getEmbedToken])

  if (!isAdmin) {
    return (
      <div className="flex h-full items-center justify-center bg-neutral-950 text-neutral-400">
        Admin access required
      </div>
    )
  }

  if (!recording) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-950">
        <Loader2 className="h-5 w-5 animate-spin text-neutral-500" />
      </div>
    )
  }

  const embedUrl = embedToken ? `https://us.posthog.com/embedded/${embedToken}` : null
  const externalUrl = recording.posthogSessionId
    ? `https://us.posthog.com/project/${POSTHOG_PROJECT_ID}/replay/${recording.posthogSessionId}`
    : null

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-neutral-950 text-neutral-100">
      <div className="flex items-center justify-between border-b border-neutral-800 px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/admin/sessions')}
            className="rounded-md p-1.5 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-200"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            {recording.userAvatar ? (
              <img src={recording.userAvatar} className="h-6 w-6 rounded-full" alt="" />
            ) : (
              <div className="flex h-6 w-6 items-center justify-center rounded-full bg-neutral-800 text-[10px] font-medium text-neutral-400">
                {(recording.userName || '?')[0].toUpperCase()}
              </div>
            )}
            <span className="text-sm font-medium">{recording.userName}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 text-xs text-neutral-500">
          <span>{formatDate(recording.startedAt)}</span>
          {externalUrl && (
            <a
              href={externalUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 text-neutral-400 hover:text-neutral-200"
            >
              Open in PostHog
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>

      <div className="relative flex-1 overflow-hidden bg-black">
        {!recording.posthogSessionId ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-neutral-500">
            <AlertTriangle className="mb-2 h-6 w-6 text-amber-500" />
            <p className="text-sm">No PostHog session linked to this recording</p>
          </div>
        ) : embedError ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-neutral-500">
            <AlertTriangle className="mb-2 h-6 w-6 text-amber-500" />
            <p className="text-sm">{embedError}</p>
          </div>
        ) : !embedUrl ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-500" />
            <p className="text-xs text-neutral-600">Loading PostHog replay…</p>
          </div>
        ) : (
          <iframe
            key={embedUrl}
            src={embedUrl}
            title="PostHog session replay"
            className="h-full w-full border-0"
            allow="clipboard-write; fullscreen"
          />
        )}
      </div>
    </div>
  )
}
